import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reduce, size } from 'lodash';
import { useApi } from 'common/hooks/api';
import { useDebouncedValue } from 'common/hooks';

const LIMIT = 5;
const DEBOUNCE_TIME = 200;

export default function useGlobalSearch() {
  const Api = useApi();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedTerm = useDebouncedValue(searchTerm, DEBOUNCE_TIME);

  const {
    data: results = {},
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['globalSearch', debouncedTerm],
    queryFn: () => Api.search({ term: debouncedTerm, limit: LIMIT + 1 }),
    enabled: debouncedTerm.length > 0,
  });

  const handleSearchChange = useCallback(value => {
    setSearchTerm(value);
  }, []);

  const handleCancel = useCallback(() => {
    setSearchTerm('');
  }, []);

  const handleLinkClick = useCallback(() => {
    setSearchTerm('');
  }, []);

  const filteredResults = useMemo(
    () =>
      reduce(
        results,
        (acc, value, key) => {
          if (size(value) !== 0) {
            acc[key] = value;
          }
          return acc;
        },
        {},
      ),
    [results],
  );

  const hasResults = searchTerm.length > 0 && size(results) > 0;
  const isLoading = searchTerm.length > 0 && (isFetching || searchTerm !== debouncedTerm);

  return {
    searchTerm,
    results,
    filteredResults,
    hasResults,
    isLoading,
    isError,
    handleSearchChange,
    handleCancel,
    handleLinkClick,
    limit: LIMIT,
  };
}
